import { useCallback, useEffect, useRef, useState } from "react";

interface Offset {
  x: number;
  y: number;
}

const ORIGIN: Offset = { x: 0, y: 0 };

/**
 * Lets a Radix `DialogContent` be repositioned by dragging a header element.
 * The dialog otherwise stays centered (via its default translate classes);
 * this hook only tracks an additional `{x, y}` pixel offset applied on top of
 * that centering, clamped so the dragged element never leaves the viewport.
 * The offset resets to the origin whenever `open` becomes false, so the next
 * open starts centered again.
 * @param {boolean} open - Whether the dialog is currently open.
 * @returns {{ offset: Offset; onHeaderPointerDown: Function }}
 *   The current offset and the pointer-down handler to attach to the header.
 */
export function useDraggableDialog(open: boolean) {
  const [offset, setOffset] = useState<Offset>(ORIGIN);
  type DragState = { pointerId: number; startX: number; startY: number; origin: Offset } | undefined;
  // eslint-disable-next-line unicorn/no-useless-undefined -- required by the useRef(initialValue) overload
  const dragState = useRef<DragState>(undefined);

  useEffect(() => {
    if (!open) {
      setOffset(ORIGIN);
      dragState.current = undefined;
    }
  }, [open]);

  useEffect(() => {
    /**
     * Updates the offset from the active drag's pointer position.
     * @param {PointerEvent} event - The window pointermove event.
     */
    function onPointerMove(event: PointerEvent) {
      const drag = dragState.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }
      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      const nextX = Math.min(Math.max(drag.origin.x + deltaX, -window.innerWidth / 2), window.innerWidth / 2);
      const nextY = Math.min(Math.max(drag.origin.y + deltaY, -window.innerHeight / 2), window.innerHeight / 2);
      setOffset({ x: nextX, y: nextY });
    }

    /**
     * Clears the active drag when its pointer is released.
     * @param {PointerEvent} event - The window pointerup event.
     */
    function onPointerUp(event: PointerEvent) {
      if (dragState.current?.pointerId === event.pointerId) {
        dragState.current = undefined;
      }
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  const onHeaderPointerDown = useCallback(
    (event: React.PointerEvent) => {
      dragState.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, origin: offset };
    },
    [offset]
  );

  return { offset, onHeaderPointerDown };
}
